import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Lesson as LessonType } from "../types";
import VoxelBackground from "../components/VoxelBackground";

export default function LessonPage() {
  const { lessonId, childId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<LessonType | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selected, setSelected] = useState<number | string | null>(null);
  const [correct, setCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(1);
  const [shuffledQuestions, setShuffledQuestions] = useState<any[]>([]);

  useEffect(() => {
    fetchLesson();
  }, []);

  const fetchLesson = async () => {
    try {
      const res = await api.get(`/lessons/${lessonId}`);
      const questions = res.data.lesson.content.questions;
      setShuffledQuestions(shuffleQuestions(questions));
      setLesson(res.data.lesson);
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const shuffleQuestions = (questions: any[]) => {
    return [...questions]
      .sort(() => Math.random() - 0.5)
      .map(q => ({
        ...q,
        options: [...q.options].sort(() => Math.random() - 0.5)
      }));
  };

  const handleAnswer = (option: number | string) => {
    if (selected !== null) return;
    setSelected(option);

    const isCorrect = option === shuffledQuestions[currentQuestion].answer;
    setCorrect(isCorrect);
    if (isCorrect) setScore((s) => s + 1);

    setTimeout(() => {
      if (currentQuestion + 1 < shuffledQuestions.length) {
        setCurrentQuestion((q) => q + 1);
        setSelected(null);
        setCorrect(null);
      } else {
        setFinished(true);
        saveProgress(isCorrect ? score + 1 : score);
      }
    }, 1500);
  };

  const saveProgress = async (finalScore: number) => {
    try {
      await api.post(`/lessons/${lessonId}/complete`, {
        childId,
        score: finalScore * 10,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const retryLesson = () => {
    if (!lesson) return;
    const questions = (lesson.content as any).questions;
    setShuffledQuestions(shuffleQuestions(questions));
    setCurrentQuestion(0);
    setScore(0);
    setFinished(false);
    setSelected(null);
    setCorrect(null);
    setAttempt(a => a + 1);
  };

  if (loading) return <div style={styles.loading}>⏳ Ładowanie...</div>;
  if (!lesson) return null;

  const question = shuffledQuestions[currentQuestion];
  const percentage = finished ? Math.round((score / shuffledQuestions.length) * 100) : 0;
  const isPerfect = percentage === 100;

  if (finished) {
    return (
      <div style={styles.finishContainer}>
        <VoxelBackground />
        <div style={styles.finishBox}>
          <div style={styles.finishEmoji}>
            {isPerfect ? "🏆" : percentage >= 50 ? "🌟" : "💪"}
          </div>
          <h2 style={styles.finishTitle}>
            {isPerfect ? "Brawo! Bezbłędnie!" : percentage >= 50 ? "Dobra robota!" : "Nie poddawaj się!"}
          </h2>
          <p style={styles.finishScore}>
            {score} / {shuffledQuestions.length} poprawnych odpowiedzi
          </p>

          {!isPerfect && (
            <div style={styles.tryAgainBox}>
              <p style={styles.tryAgainText}>
                🎯 Zdobądź 100% żeby przejść dalej!
              </p>
              <p style={styles.attemptText}>Próba #{attempt}</p>
            </div>
          )}

          {isPerfect && (
            <div style={styles.perfectBox}>
              <p style={styles.perfectText}>
                ⭐ Perfekcyjny wynik! Możesz przejść do następnej lekcji!
              </p>
              <div style={styles.pointsEarned}>
                +{score * 10} punktów zdobytych! ⭐
              </div>
            </div>
          )}

          {!isPerfect && (
            <div style={styles.pointsEarned}>
              +{score * 10} punktów zdobytych! ⭐
            </div>
          )}

          <div style={styles.finishButtons}>
            {!isPerfect && (
              <button style={styles.retryBtn} onClick={retryLesson}>
                Spróbuj ponownie 🔄 (próba #{attempt + 1})
              </button>
            )}
            <button
              style={styles.backBtn}
              onClick={() => navigate(`/child/${childId}`)}
            >
              {isPerfect ? "Następna lekcja! 🚀" : "Wróć do lekcji 📚"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <VoxelBackground />
      <div style={styles.header}>
        <button onClick={() => navigate(`/child/${childId}`)} style={styles.headerBack}>
          ← Wróć
        </button>
        <h2 style={styles.lessonTitle}>{lesson.title}</h2>
        <div style={styles.progress}>
          {currentQuestion + 1} / {shuffledQuestions.length}
        </div>
      </div>

      <div style={styles.progressBar}>
        <div
          style={{
            ...styles.progressFill,
            width: `${(currentQuestion / shuffledQuestions.length) * 100}%`,
          }}
        />
      </div>

      {attempt > 1 && (
        <div style={styles.attemptBanner}>
          🔄 Próba #{attempt} — dasz radę!
        </div>
      )}

      <div style={styles.content}>
        <div style={styles.mascot}>🤖</div>

        <div style={styles.questionBox}>
          <p style={styles.questionText}>{question?.question}</p>
        </div>

        <div style={styles.optionsGrid}>
          {question?.options.map((option: number | string, index: number) => {
            let bgColor = "white";
            if (selected !== null) {
              if (option === question.answer) bgColor = "#e8f5e9";
              else if (option === selected) bgColor = "#ffebee";
            }

            return (
              <button
                key={index}
                style={{
                  ...styles.optionBtn,
                  background: bgColor,
                  border: selected !== null && option === question.answer
                    ? "3px solid #4caf50"
                    : selected === option
                    ? "3px solid #f44336"
                    : "3px solid #eee",
                }}
                onClick={() => handleAnswer(option)}
              >
                {option}
              </button>
            );
          })}
        </div>

        {correct !== null && (
          <div style={{
            ...styles.feedback,
            background: correct ? "#e8f5e9" : "#ffebee",
            color: correct ? "#2e7d32" : "#c62828",
          }}>
            {correct ? "✅ Świetnie! Brawo!" : "❌ Nie tym razem!"}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    background: "#87CEEB",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "16px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },
  headerBack: {
    background: "rgba(255,255,255,0.2)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "16px",
  },
  lessonTitle: { color: "white", margin: 0, fontSize: "20px" },
  progress: { color: "white", fontSize: "16px" },
  progressBar: {
    height: "8px",
    background: "#ddd",
    position: "relative",
    zIndex: 2,
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    transition: "width 0.3s ease",
  },
  attemptBanner: {
    background: "#fff3cd",
    color: "#f57f17",
    padding: "8px",
    textAlign: "center",
    fontSize: "16px",
    position: "relative",
    zIndex: 2,
  },
  content: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: "32px",
    textAlign: "center",
    position: "relative",
    zIndex: 1,
  },
  mascot: { fontSize: "80px", marginBottom: "24px" },
  questionBox: {
    background: "white",
    borderRadius: "20px",
    padding: "32px",
    marginBottom: "24px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  questionText: { fontSize: "28px", color: "#333", margin: 0 },
  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "24px",
  },
  optionBtn: {
    padding: "24px",
    borderRadius: "16px",
    fontSize: "24px",
    cursor: "pointer",
    transition: "all 0.2s",
    fontWeight: "bold",
    color: "#333",
  },
  feedback: {
    padding: "16px",
    borderRadius: "12px",
    fontSize: "20px",
    fontWeight: "bold",
  },
  finishContainer: {
    minHeight: "100vh",
    background: "#87CEEB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  finishBox: {
    background: "white",
    borderRadius: "24px",
    padding: "48px",
    textAlign: "center",
    maxWidth: "400px",
    width: "100%",
    position: "relative",
    zIndex: 1,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  finishEmoji: { fontSize: "80px", marginBottom: "16px" },
  finishTitle: { fontSize: "28px", color: "#333", marginBottom: "8px" },
  finishScore: { fontSize: "20px", color: "#888", marginBottom: "16px" },
  tryAgainBox: {
    background: "#ffebee",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "16px",
  },
  tryAgainText: {
    color: "#c62828",
    fontSize: "16px",
    margin: "0 0 4px",
  },
  attemptText: {
    color: "#888",
    fontSize: "14px",
    margin: 0,
  },
  perfectBox: {
    background: "#e8f5e9",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "16px",
  },
  perfectText: {
    color: "#2e7d32",
    fontSize: "16px",
    margin: "0 0 8px",
  },
  pointsEarned: {
    background: "#fff3cd",
    borderRadius: "12px",
    padding: "12px",
    fontSize: "18px",
    marginBottom: "24px",
    color: "#f57f17",
  },
  finishButtons: { display: "flex", flexDirection: "column", gap: "12px" },
  retryBtn: {
    padding: "14px",
    borderRadius: "12px",
    border: "2px solid #667eea",
    background: "white",
    color: "#667eea",
    fontSize: "16px",
    cursor: "pointer",
  },
  backBtn: {
    padding: "14px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontSize: "16px",
    cursor: "pointer",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    fontSize: "24px",
  },
};